import React, { useMemo, useState } from "react";
import {
    Box,
    CircularProgress,
    Collapse,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { BsArrowClockwise, BsEye, BsTable } from "react-icons/bs";
import { MdKeyboardArrowDown, MdKeyboardArrowRight, MdSearch } from "react-icons/md";

const MAX_VISIBLE_TABLES = 200;

const qualifiedName = (table) => (table.schema === "public" ? table.name : `${table.schema}.${table.name}`);

export default function SchemaBrowser({ tables, loading, error, onRefresh, onInsert }) {
    const [search, setSearch] = useState("");
    const [expanded, setExpanded] = useState({});

    const term = search.trim().toLowerCase();

    // A table matches on its own name or on any column name; when the match is
    // on columns only, those columns are listed so the user sees why.
    const filtered = useMemo(() => {
        if (!term) return tables.map((table) => ({ table, matchedColumns: null }));
        return tables.reduce((acc, table) => {
            const nameMatch = qualifiedName(table).toLowerCase().includes(term);
            const matchedColumns = table.columns.filter((col) => col.name.toLowerCase().includes(term));
            if (nameMatch || matchedColumns.length) {
                acc.push({ table, matchedColumns: nameMatch ? null : matchedColumns });
            }
            return acc;
        }, []);
    }, [tables, term]);

    const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", p: 1.5, pb: 1 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search tables or columns"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <MdSearch size={18} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Tooltip title="Reload schema">
                    <span>
                        <IconButton size="small" onClick={onRefresh} disabled={loading}>
                            <BsArrowClockwise size={16} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Typography sx={{ px: 1.5, pb: 1, fontSize: 12, color: "text.secondary" }}>
                {loading
                    ? "Loading schema..."
                    : `${filtered.length} of ${tables.length} tables · click a name to insert it`}
            </Typography>

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {!loading && error && (
                    <Typography sx={{ p: 2, fontSize: 13, color: "error.main" }}>{error}</Typography>
                )}
                {!loading && !error && (
                    <List dense disablePadding>
                        {filtered.slice(0, MAX_VISIBLE_TABLES).map(({ table, matchedColumns }) => {
                            const key = `${table.schema}.${table.name}`;
                            const isOpen = Boolean(expanded[key]) || Boolean(matchedColumns);
                            const columns = matchedColumns || table.columns;
                            return (
                                <React.Fragment key={key}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <IconButton size="small" onClick={() => toggle(key)} aria-label={isOpen ? "Collapse" : "Expand"}>
                                            {isOpen ? <MdKeyboardArrowDown size={18} /> : <MdKeyboardArrowRight size={18} />}
                                        </IconButton>
                                        <ListItemButton
                                            sx={{ py: 0.25, pl: 0.5, borderRadius: 1, gap: 1 }}
                                            onClick={() => onInsert(qualifiedName(table))}
                                        >
                                            {table.type === "view"
                                                ? <BsEye size={13} color="#7b1fa2" title="View" />
                                                : <BsTable size={13} color="#0F4F96" title="Table" />}
                                            <Typography sx={{ fontSize: 13, fontFamily: "monospace", wordBreak: "break-all" }}>
                                                {qualifiedName(table)}
                                            </Typography>
                                        </ListItemButton>
                                    </Box>
                                    <Collapse in={isOpen} unmountOnExit>
                                        <List dense disablePadding sx={{ pl: 5 }}>
                                            {columns.map((col) => (
                                                <ListItemButton
                                                    key={col.name}
                                                    sx={{ py: 0, borderRadius: 1, justifyContent: "space-between", gap: 1 }}
                                                    onClick={() => onInsert(col.name)}
                                                >
                                                    <Typography sx={{ fontSize: 12, fontFamily: "monospace", wordBreak: "break-all" }}>
                                                        {col.name}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 11, color: "text.secondary", whiteSpace: "nowrap" }}>
                                                        {col.type}
                                                    </Typography>
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    </Collapse>
                                </React.Fragment>
                            );
                        })}
                        {filtered.length > MAX_VISIBLE_TABLES && (
                            <Typography sx={{ p: 1.5, fontSize: 12, color: "text.secondary" }}>
                                Showing the first {MAX_VISIBLE_TABLES}. Refine the search to see more.
                            </Typography>
                        )}
                    </List>
                )}
            </Box>
        </Box>
    );
}
